import { CodecError, decodePoem, encodeMessage, estimateLineCount } from "./codec/codec";
import { MAX_MESSAGE_BYTES } from "./codec/constants";
import { PoemFormatError, scanPoem } from "./codec/format";
import { BrowserCorpus } from "./corpus/browser-corpus";
import type { Corpus, InstallProgress } from "./corpus/types";

type Mode = "encode" | "decode";

const encoder = new TextEncoder();
const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
const CORPUS_LABEL = "cn-1 · 句库 2,097,152";

export function countCharacters(value: string): number {
  return Array.from(graphemeSegmenter.segment(value)).length;
}

function mustElement<T extends HTMLElement>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing interface element: ${selector}`);
  return element;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/gu, (character) => {
    const entities: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" };
    return entities[character]!;
  });
}

export function verseHtml(poem: string): string {
  const lines = poem.split(/\r?\n/u).filter(Boolean);
  if (lines.length < 2) return `<p class="empty-sheet">诗成以后，落在这里。</p>`;
  return `
    <p class="poem-title">${escapeHtml(lines[0]!)}</p>
    <div class="verse-lines">${lines
      .slice(1)
      .map((line) => `<p class="couplet-row${Array.from(line).length > 16 ? " is-long" : ""}">${escapeHtml(line)}</p>`)
      .join("")}</div>
  `;
}

export class PoemCodecApp {
  private mode: Mode = "encode";
  private corpus?: Corpus;
  private corpusFailure?: Error;
  private working = false;
  private operationGeneration = 0;

  private readonly input: HTMLTextAreaElement;
  private readonly username: HTMLInputElement;
  private readonly secret: HTMLInputElement;
  private readonly action: HTMLButtonElement;
  private readonly copy: HTMLButtonElement;
  private readonly output: HTMLElement;
  private readonly outputMeta: HTMLElement;
  private readonly status: HTMLElement;
  private readonly estimate: HTMLElement;
  private readonly inputMeta: HTMLElement;
  private readonly inputLabel: HTMLElement;
  private readonly inputHint: HTMLElement;
  private readonly secretHint: HTMLElement;
  private readonly modeButtons: NodeListOf<HTMLButtonElement>;

  constructor(root: HTMLElement) {
    root.innerHTML = this.template();
    this.input = mustElement(root, "#source-text");
    this.username = mustElement(root, "#username");
    this.secret = mustElement(root, "#secret");
    this.action = mustElement(root, "#primary-action");
    this.copy = mustElement(root, "#copy-poem");
    this.output = mustElement(root, "#poem-output");
    this.outputMeta = mustElement(root, "#output-meta");
    this.status = mustElement(root, "#status");
    this.estimate = mustElement(root, "#line-estimate");
    this.inputMeta = mustElement(root, "#input-meta");
    this.inputLabel = mustElement(root, "#input-label");
    this.inputHint = mustElement(root, "#input-hint");
    this.secretHint = mustElement(root, "#secret-hint");
    this.modeButtons = root.querySelectorAll<HTMLButtonElement>("[data-mode]");
    this.bindEvents();
    this.renderMode();
    void this.prepareCorpus();
  }

  private template(): string {
    return `
      <div class="page-grain" aria-hidden="true"></div>
      <header class="masthead">
        <a class="wordmark" href="./" aria-label="诗帖首页">
          <span class="wordmark-cn">诗帖</span>
          <span class="wordmark-en">POEM—CODEC / 壹</span>
        </a>
        <p class="masthead-note">真正的旧句，藏一封新信。</p>
        <span class="privacy-mark"><i></i> 本地运算 · 无痕</span>
      </header>

      <main class="workspace">
        <section class="writing-desk" aria-labelledby="desk-title">
          <div class="section-kicker"><span>01</span> 写信</div>
          <h1 id="desk-title">把一句话，<br><em>藏进诗里。</em></h1>
          <p class="lede">文字不会离开这台设备。诗句安装一次，此后断网也能写，也能读。</p>

          <div class="mode-switch" role="group" aria-label="工作方式">
            <button type="button" data-mode="encode" aria-pressed="true">写成诗</button>
            <button type="button" data-mode="decode" aria-pressed="false">读出信</button>
          </div>

          <div class="field source-field">
            <div class="label-row">
              <label id="input-label" for="source-text">要藏的文字</label>
              <span id="input-meta">0 字符 · 0 B</span>
            </div>
            <textarea id="source-text" rows="6" spellcheck="false" aria-describedby="input-hint" placeholder="见字如面。"></textarea>
            <p id="input-hint">任意语言、符号和换行都会原样回来。</p>
          </div>

          <div class="key-fields">
            <div class="field">
              <label for="username">名字</label>
              <input id="username" autocomplete="off" placeholder="收信人知道的名字" />
            </div>
            <div class="field">
              <label for="secret">暗语</label>
              <input id="secret" type="password" autocomplete="off" aria-describedby="secret-hint" placeholder="两个人知道的秘密" />
              <p id="secret-hint" class="field-help"></p>
            </div>
          </div>

          <div class="action-row">
            <button id="primary-action" class="seal-button" type="button"><span>成帖</span></button>
            <div class="estimate-block">
              <span id="line-estimate"></span>
              <small>行数会透露信的长短</small>
            </div>
          </div>
          <p id="status" class="status" role="status" aria-live="polite">正在查看诗库…</p>
        </section>

        <section class="poem-stage" aria-labelledby="poem-heading">
          <h2 id="poem-heading" class="visually-hidden">诗帖输出</h2>
          <div class="section-kicker light"><span>02</span> 诗帖</div>
          <div class="sheet-wrap">
            <article class="poem-sheet" id="poem-output" aria-labelledby="poem-heading">
              <p class="empty-sheet">诗成以后，落在这里。</p>
            </article>
            <span class="sheet-shadow" aria-hidden="true"></span>
          </div>
          <div class="poem-actions">
            <button id="copy-poem" type="button" disabled>抄下诗帖</button>
            <div class="output-facts">
              <span id="output-meta" class="output-meta">诗帖 · 0 字符</span>
              <span>${CORPUS_LABEL}</span>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <p>这是一枚好玩的密码盒，不是保险箱。相同的文字与钥匙，会写出相同的诗。</p>
        <div class="footer-meta">
          <p>诗句来自公开古典诗词集 · codec v1</p>
          <p class="release-mark">
            <span>${escapeHtml(__APP_UPDATE__)}</span><span>·</span><code>${escapeHtml(__COMMIT_HASH__)}</code>
          </p>
        </div>
      </footer>
    `;
  }

  private bindEvents(): void {
    this.modeButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.mode = button.dataset.mode as Mode;
        this.renderMode();
      });
    });
    for (const field of [this.input, this.username, this.secret]) {
      field.addEventListener("input", () => this.handleDependentInput());
      field.addEventListener("change", () => this.handleDependentInput());
    }
    this.action.addEventListener("click", () => void this.run());
    this.copy.addEventListener("click", () => void this.copyOutput());
  }

  private async prepareCorpus(): Promise<void> {
    try {
      this.corpus = await BrowserCorpus.load();
      await this.corpus.ensureReady((progress) => this.renderCorpusProgress(progress));
      this.setStatus("诗库已在本机备妥。", "ready");
    } catch (error) {
      this.corpusFailure = error instanceof Error ? error : new Error(String(error));
      this.setStatus("完整诗库尚未构建；请先运行语料生成命令。", "error");
    }
  }

  private renderCorpusProgress(progress: InstallProgress): void {
    if (progress.phase === "ready") {
      this.setStatus("诗库已在本机备妥。", "ready");
      return;
    }
    const percentage = Math.round((progress.complete / Math.max(progress.total, 1)) * 100);
    this.setStatus(progress.phase === "download" ? `初次收录诗句… ${percentage}%` : `编排本地索引… ${percentage}%`, "loading");
  }

  private renderMode(): void {
    const isEncode = this.mode === "encode";
    this.modeButtons.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.mode === this.mode)));
    this.inputLabel.textContent = isEncode ? "要藏的文字" : "收到的诗帖";
    this.inputHint.textContent = isEncode
      ? "任意语言、符号和换行都会原样回来。"
      : "题签可以省略；换行、句读、繁简与句中空白不妨碍读信。";
    this.input.placeholder = isEncode ? "见字如面。" : "〔诗帖 v1·cn-1〕\n……";
    this.action.querySelector("span")!.textContent = isEncode ? "成帖" : "开帖";
    this.copy.textContent = isEncode ? "抄下诗帖" : "抄下原文";
    this.renderSecretHint();
    this.invalidateOutput();
    this.renderEstimate();
    this.restoreNeutralStatus();
  }

  private handleDependentInput(): void {
    this.invalidateOutput();
    this.renderSecretHint();
    this.renderEstimate();
    this.restoreNeutralStatus();
  }

  private emptySheet(): string {
    return this.mode === "encode" ? "诗成以后，落在这里。" : "信读出来，落在这里。";
  }

  private invalidateOutput(): void {
    this.operationGeneration += 1;
    delete this.output.dataset.raw;
    this.output.innerHTML = `<p class="empty-sheet">${this.emptySheet()}</p>`;
    this.outputMeta.textContent = `${this.mode === "encode" ? "诗帖" : "原文"} · 0 字符`;
    this.copy.disabled = true;
    this.copy.textContent = this.mode === "encode" ? "抄下诗帖" : "抄下原文";
  }

  private renderSecretHint(): void {
    if (this.secret.value) {
      this.secretHint.hidden = true;
      this.secretHint.textContent = "";
      return;
    }
    this.secretHint.hidden = false;
    this.secretHint.dataset.tone = this.mode === "encode" ? "warning" : "neutral";
    this.secretHint.textContent =
      this.mode === "encode" ? "不填暗语，知道你名字的人就能读。" : "若写帖时没有暗语，这里也留空。";
  }

  private restoreNeutralStatus(): void {
    if (this.corpus) this.setStatus("诗库已在本机备妥。", "ready");
    else if (this.corpusFailure) this.setStatus("完整诗库尚未构建；请先运行语料生成命令。", "error");
    else this.setStatus("正在查看诗库…", "loading");
  }

  private renderEstimate(): void {
    const bytes = encoder.encode(this.input.value).length;
    const characters = countCharacters(this.input.value);
    this.inputMeta.textContent = `${characters.toLocaleString("zh-CN")} 字符 · ${bytes.toLocaleString("zh-CN")} B`;
    if (this.mode === "encode") {
      if (!this.input.value) {
        this.estimate.textContent = "";
        return;
      }
      const sentences = estimateLineCount(this.input.value);
      this.estimate.textContent = bytes > MAX_MESSAGE_BYTES ? "文字太长" : `约 ${sentences / 2} 联 · ${sentences} 句`;
    } else {
      const scan = scanPoem(this.input.value);
      if (scan.state === "empty" || scan.state === "title-partial") {
        this.estimate.textContent = "";
      } else if (scan.state === "title-invalid") {
        this.estimate.textContent = "题签认不出";
      } else if (scan.sentences === 1) {
        this.estimate.textContent = "约 1 句";
      } else {
        this.estimate.textContent = `约 ${scan.rows} 联 · ${scan.sentences} 句`;
      }
    }
  }

  private setStatus(message: string, state: "loading" | "ready" | "error" | "working" = "ready"): void {
    this.status.textContent = message;
    this.status.dataset.state = state;
  }

  private async run(): Promise<void> {
    if (this.working) return;
    this.invalidateOutput();
    if (!this.corpus) {
      this.setStatus(this.corpusFailure?.message ?? "诗库尚未准备好。", "error");
      return;
    }
    if (!this.username.value) {
      this.setStatus("请填写名字。", "error");
      this.username.focus();
      return;
    }

    const operation = ++this.operationGeneration;
    const mode = this.mode;
    const source = this.input.value;
    const username = this.username.value;
    const secret = this.secret.value;
    this.working = true;
    this.action.disabled = true;
    this.setStatus(mode === "encode" ? "正在拈句成诗…" : "正在从诗里读信…", "working");
    try {
      const result =
        mode === "encode"
          ? await encodeMessage(source, username, secret, this.corpus)
          : await decodePoem(source, username, secret, this.corpus);
      if (operation !== this.operationGeneration) return;
      this.output.dataset.raw = result;
      this.output.innerHTML = mode === "encode" ? verseHtml(result) : `<p class="decoded-letter">${escapeHtml(result)}</p>`;
      this.outputMeta.textContent = `${mode === "encode" ? "诗帖" : "原文"} · ${countCharacters(result).toLocaleString("zh-CN")} 字符`;
      this.copy.disabled = false;
      this.setStatus(mode === "encode" ? "诗帖已成。" : "信已经读出来了。", "ready");
    } catch (error) {
      if (operation !== this.operationGeneration) return;
      this.invalidateOutput();
      const known = error instanceof CodecError || error instanceof PoemFormatError;
      this.setStatus(known ? error.message : "没有写成。请稍后再试。", "error");
      if (!known) console.error(error);
    } finally {
      this.working = false;
      this.action.disabled = false;
    }
  }

  private async copyOutput(): Promise<void> {
    const value = this.output.dataset.raw;
    if (value === undefined) return;
    try {
      await navigator.clipboard.writeText(value);
      const previous = this.copy.textContent;
      this.copy.textContent = "已经抄好";
      this.setStatus("已经抄到剪贴板。", "ready");
      window.setTimeout(() => {
        if (this.output.dataset.raw === value) this.copy.textContent = previous;
      }, 1_600);
    } catch {
      this.setStatus("没能写入剪贴板，请选中文字手动复制。", "error");
    }
  }
}
